<script>
	import { getContext } from "svelte";
	import { fade, blur, fly, slide, scale } from "svelte/transition";
	import { tweened } from "svelte/motion";
	import { cubicOut } from "svelte/easing";

	import { percent, px } from "../utils";
	import { chartOrientation, animationParameters, inOutSectionsById, colors } from "../stores";

	const { data, xGet, yGet, y, yScale, yDomain, yRange, xDomain, xRange, width, height } =
		getContext("LayerCake");

	export let color = (d, i) => "#F00";
	export let label = (d, i) => "";
	export let direction;
	export let barWidth = 40;
	export let title = "";
	export let subtitle = "";
	export let formatValue = (d) => d;
	export let compareValue = null;
	export let compareLabel = "";

	const isIncoming = direction === "incoming";

	// calculate size of each part of the bar
	$: sizes = $data[0].map((d) => $yGet(d)[0] - $yGet(d)[1]);

	// create tweened sizes
	const tSizes = tweened(sizes, $animationParameters);
	$: tSizes.set(sizes);

	$: total = $data[0].reduce((prev, curr) => prev + curr.data.value, 0);

	const tTotal = tweened(total, $animationParameters);
	$: tTotal.set(total);

	let selected = "";

	const mouseover = (e) => {
		selected = e.srcElement.id;
	};
	const mouseout = (e) => {
		selected = "";
	};
</script>

<div class="container" class:hovering={selected !== ""}>
	<div class="left">
		<div class="heading" transition:fade style:color={$inOutSectionsById[direction].color}>
			<span class="title">{title}</span><br /><span class="subtitle">{subtitle}</span><br /><span
				class="value">{formatValue($tTotal)}</span
			>
		</div>
		<div class="labels" transition:fade>
			{#each $data[0] as d, i}
				{#if d.data.value > 0}
					<div
						class="label"
						class:selected={selected === d.data.id}
						id={d.data.id}
						style:color={$colors[direction][d.data.id]}
						on:mouseover={mouseover}
						on:mouseout={mouseout}
						transition:fade
					>
						{label(d.data, i)}
					</div>
				{/if}
			{/each}
		</div>
	</div>
	<div
		class="bar"
		style:width={px(barWidth)}
		class:incoming={isIncoming}
		class:outgoing={!isIncoming}
		transition:fade={$animationParameters}
	>
		<div class="gradient" />
		<!-- <div class="line top" /> -->
		<div class="line bottom" />
		{#if compareValue}
			<div class="compare-container" style:top={percent($yScale(compareValue))}>
				<div class="line" />
				<div class="label">{compareLabel}</div>
			</div>
		{/if}

		{#each $data[0] as d, i}
			{@const isSelected = selected === d.data.id}
			<div
				class="part"
				class:selected={isSelected}
				id={d.data.id}
				style:height={$chartOrientation === "horizontal" ? percent($tSizes[i]) : ""}
				style:background={color(d.data, i)}
				on:mouseover={mouseover}
				on:mouseout={mouseout}
			>
				<!-- {label(d.data, i)}<br />{percent(frmtF($tSizes[i]))} -->
				<!-- {#if $tSizes[i] > 0}
			{label(d.data, i)}<br />{formatValue(d.data.value)}
			{/if} -->
				{#if isSelected}
					<div transition:fade style:color={$colors[direction][d.data.id]} class="tooltip">
						{formatValue(d.data.value)}
					</div>
				{/if}
			</div>
		{/each}
	</div>
</div>

<style lang="scss">
	.container {
		display: flex;
		width: 100%;
		height: 100%;
	}

	.left {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		width: 100%;
	}

	.labels {
		font-weight: 500;
		font-size: 10px;
		line-height: 11px;
		letter-spacing: 0.02em;
		z-index: 3;
		position: relative;
		max-width: calc(100% - 18px);
		-webkit-tap-highlight-color: transparent;
		user-select: none;

		.label {
			background: #fff;
			border-radius: 5px;
			width: fit-content;
			padding: 1px 3px;
			margin: 4px 0;
			cursor: pointer;
		}
	}

	@media (min-width: 840px) {
		.labels,
		.tooltip {
			font-size: 11.5px !important;
			line-height: 11px !important;
		}

		.tooltip {
			transform: translate(-3px, -100%) !important;
		}

		.heading {
			font-size: 11.5px !important;
			line-height: 11px !important;

			.title {
				font-size: 15px !important;
				line-height: 19px !important;
			}

			.value {
				font-size: 15px !important;
				line-height: 27px !important;
			}
		}
	}

	.tooltip {
		pointer-events: none;
		font-weight: 500;
		font-size: 10px;
		line-height: 11px;
		transform: translate(-1px, -100%);
		text-align: center;
		text-shadow: 2px 0 0 #fff, -2px 0 0 #fff, 0 2px 0 #fff, 0 -2px 0 #fff, 1px 1px #fff,
			-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff;
		pointer-events: none;
	}

	.heading {
		font-size: 11.5px;
		letter-spacing: 0.01em;
		line-height: 13px;
		text-shadow: 0px 1px 1px #fff, 0px -1px 1px #fff, 1px 0px 1px #fff, 0px 0px 1px #fff;
		z-index: 2;
		max-width: calc(100% - 18px);
	}

	:global(.last) .heading {
		max-width: calc(100% - 50px);
	}

	.title {
		font-weight: 600;
	}

	.subtitle {
	}

	.value {
		font-weight: 600;
	}

	.bar {
		display: flex;
		width: 100%;
		height: 100%;
		position: absolute;
		background: linear-gradient(180deg, rgba(242, 242, 242, 0) 0%, #e1e1e1 14.06%);
		flex-direction: column;
		justify-content: flex-end;
		right: 0;
	}

	.hovering {
		.part.selected,
		.label.selected {
			opacity: 1;
		}

		.part:not(.selected),
		.label:not(.selected) {
			opacity: 0.25;
		}
	}

	.part {
		z-index: 1;
		// min-width: 27px;
		border-top: 1px solid #f2f2f2;
		cursor: pointer;
		-webkit-tap-highlight-color: transparent;
		user-select: none;
	}

	.gradient {
		width: 5px;
		height: 100%;
		background: linear-gradient(90deg, #d9d9d9 0%, rgba(217, 217, 217, 0) 100%);
		position: absolute;
		// z-index: -1;
	}

	.compare-container {
		position: absolute;
		width: 100%;
		color: #2a2a2a;

		.line {
			border: 0.5px solid #2a2a2a;
		}

		.label {
			font-weight: 400;
			font-size: 10px;
			line-height: 11px;
			letter-spacing: 0.04em;
			transform: translate(-190%, -50%);
		}
	}

	.line {
		mix-blend-mode: multiply;
		width: 25px;
		transform: translateX(-4px);
		position: absolute;
		box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.25);

		&.top {
			border: 0.5px solid #b5b5b5;
			top: -1px;
		}

		&.bottom {
			border: 0.5px solid #1f1f1f;
			bottom: -2px;
		}
	}
</style>
